import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../Editor.css';
import Tutorial from './Tutorial';

function PoemList() {
    const [poems, setPoems] = useState([]);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const { search } = useLocation();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const pid = query.get("pid") || localStorage.getItem("pid") || "";

    const [showTutorial, setShowTutorial] = useState(false);


    const fetchEligible = useCallback(async () => {
        if (!pid) return;

        setError("");
        try {
            const res = await fetch(`/api/poems/eligible?pid=${encodeURIComponent(pid)}`);
            const data = await res.json().catch(() => null);

            if (!res.ok || !data?.success) {
                throw new Error(data?.error || "Failed to load eligible poems.");
            }

            setPoems(data.poems || []);
        } catch (e) {
            setError(e.message || "Server error loading poems.");
        }
    }, [pid]);

    useEffect(() => {
        fetchEligible();
    }, [fetchEligible, location.key]); // location.key changes when you navigate back

    // refresh periodically so newly created poems appear automatically
    useEffect(() => {
        const t = setInterval(fetchEligible, 15000);
        return () => clearInterval(t);
    }, [fetchEligible]);

    const handleAnnotate = (poemId) => {
        const sp = new URLSearchParams();
        sp.set("pid", pid);
        sp.set("interface", "annotate");

        navigate({
            pathname: `/annotation/${poemId}`,
            search: `?${sp.toString()}`
        });
    };

    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    const handleBackClick = () => {
        setShowLogoutConfirm(true);
    };

    const handleConfirmYes = () => {
        // log out / clear session-like data
        localStorage.removeItem("pid");
        localStorage.removeItem("scenario");
        localStorage.removeItem("scenarioLocked");
        localStorage.removeItem("screening_ok");
        localStorage.removeItem("consent_ok");

        // go to Launcher.js home
        navigate("/", { replace: true });
    };

    const handleConfirmNo = () => {
        setShowLogoutConfirm(false);
    };

    return (
        <div className="page-container">
            {/* Top controls */}
            <button className="back-button" onClick={handleBackClick}>
                ← Back
            </button>

            <div className="header-controls" style={{padding: 0, marginLeft: 0}}>
                <h2 style={{margin: 0}}>Available poems to annotate</h2>

                <div style={{display: "flex", gap: 10, alignItems: "center"}}>
                    <button
                        className="tutorial-button"
                        type="button"
                        onClick={() => setShowTutorial(true)}
                    >
                        Tutorial
                    </button>

                    <button
                        className="publish-button"
                        type="button"
                        onClick={fetchEligible}
                        disabled={!pid}
                        style={{marginTop: 0}}
                        title={!pid ? "Missing participant ID" : "Refresh list"}
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {showTutorial && (
                <Tutorial
                    inline
                    onClose={() => setShowTutorial(false)}
                    mode="annotate"
                    type="poem_list"
                />
            )}

            {error && (
                <div className="error-message" style={{marginTop: 12}}>
                    {error}
                </div>
            )}

            {/* Empty state */}
            {poems.length === 0 ? (
                <div style={{marginTop: 16, background: "#fff", borderRadius: 8, padding: 16}}>
                    <div style={{fontWeight: 600, marginBottom: 6}}>No eligible poems right now</div>
                    <div style={{color: "#666"}}>
                        New poems may appear automatically. You can also click <b>Refresh</b>.
                    </div>
                </div>
            ) : (
                <div style={{marginTop: 16}}>
                    <table className="poem-table">
                        <thead>
                        <tr>
                            <th style={{width: 80}}>ID</th>
                            <th>Preview</th>
                            <th style={{width: 220}}>Action</th>
                        </tr>
                        </thead>

                        <tbody>
                        {poems.map((p) => {
                            const content = (p.content || "").trim();
                            const preview =
                                content.length > 180 ? `${content.slice(0, 180)}…` : content;

                            return (
                                <tr key={p.id}>
                                    <td>{p.id}</td>

                                    <td style={{whiteSpace: "pre-wrap"}}>
                                        {/* preview always visible */}
                                        <div>{preview}</div>

                                        {/* optional expand (no extra css needed) */}
                                        {content.length > 180 && (
                                            <details style={{marginTop: 6}}>
                                                <summary style={{cursor: "pointer"}}>Read full poem</summary>
                                                <div style={{marginTop: 8, whiteSpace: "pre-wrap"}}>
                                                    {content}
                                                </div>
                                            </details>
                                        )}
                                    </td>

                                    <td>
                                        <button
                                            className="publish-button"
                                            type="button"
                                            style={{marginTop: 0}}
                                            onClick={() => handleAnnotate(p.id)}
                                        >
                                            Annotate
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>

                    <div style={{marginTop: 10, color: "#666", fontSize: 14}}>
                        Showing <b>{poems.length}</b> eligible poem(s). The list refreshes automatically.
                    </div>
                </div>
            )}
            {showLogoutConfirm && (
                <div className="modal-overlay">
                    <div className="modal-card">
                        <h3 className="modal-title">Leave and log out?</h3>
                        <p className="modal-text">
                            Are you sure? You will be logged out and returned to the main menu.
                        </p>
                        <div className="modal-actions">
                            <button className="modal-btn modal-cancel" onClick={handleConfirmNo}>
                                Cancel
                            </button>
                            <button className="modal-btn modal-danger" onClick={handleConfirmYes}>
                                Yes, log out
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );

}

export default PoemList;